import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Button, Grid, Skeleton, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useState } from 'react';
import moment from 'moment';
import RefreshIcon from '@mui/icons-material/Refresh';
import { extendedDashboardFetcher } from '../../orders/dashboards/ExtendedDashboard';
import { PeriodStats } from './PeriodStat';
import { PendingPaymentLinksIndicator } from './PendingPaymentLinksIndicator';
import { PendingExternalVouchersIndicator } from './PendingExternalVouchersIndicator';
import { ExpiringPassesIndicator } from './ExpiringPassesIndicator';
import { SquareDisconnectedIndicator } from './SquareDisconnectedIndicator';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData } from '../../api';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { mb } from '../../common/theme/mbTokens';

export const CompactStats: React.FC = () => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { t } = useTranslation();
  const [lastFetch, setLastFetch] = useState<moment.Moment>(moment());
  const [isRefetching, setIsRefetching] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data,
    isLoading: swrLoading,
    mutate: refetch,
  } = useSWR({ opId: 'getExtendedDashboard' }, extendedDashboardFetcher, {
    refreshInterval: 300000,
  });
  const handleRefresh = async () => {
    setIsRefetching(true);
    await refetch();
    setLastFetch(moment());
    setIsRefetching(false);
  };
  const isLoading = swrLoading || isRefetching;

  return (
    <Grid
      container
      alignItems="center"
      gap={1.5}
      sx={{
        p: 0,
        paddingTop: mb.layout.pageInset,
        paddingInline: mb.layout.pageInset,
        justifyContent: 'space-between',
      }}>
      <Grid container alignItems="center" gap={1.5} sx={{ flex: 1 }}>
        {isLoading ? (
          <Skeleton width={isSmallScreen ? 80 : 250} height={isSmallScreen ? 28 : 40} />
        ) : (
          <PeriodStats data={data?.today} label={t('dashboard.today')} window="today" />
        )}
        {isLoading ? (
          <Skeleton width={isSmallScreen ? 80 : 250} height={isSmallScreen ? 28 : 40} />
        ) : (
          <PeriodStats data={data?.yesterday} label={t('dashboard.yesterday')} window="yesterday" />
        )}
        {isLoading ? (
          <Skeleton width={140} height={24} />
        ) : (
          <Grid item>
            <Button
              variant="text"
              size="small"
              startIcon={<RefreshIcon sx={{ fontSize: '16px' }} />}
              onClick={handleRefresh}
              disabled={isLoading}
              sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '13px', minHeight: 0 }}>
              {t('dashboard.lastUpdate', { time: lastFetch.format(getTimeFormat(account)) })}
            </Button>
          </Grid>
        )}
      </Grid>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <SquareDisconnectedIndicator />
        <ExpiringPassesIndicator />
        <PendingPaymentLinksIndicator />
        <PendingExternalVouchersIndicator />
      </Stack>
    </Grid>
  );
};
